import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Star,
  TrendingUp,
  Shield,
  Clock,
  Gift,
  Award,
  ArrowRight,
  Check,
  Zap,
  Trophy,
  Sparkles,
  CreditCard,
  Smartphone,
  Gamepad2,
  Users,
  MessageCircle,
  HelpCircle,
  TrendingDown,
  DollarSign,
  Lock,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const Home: React.FC = () => {
  const topOffers = [
    {
      id: 'bwin',
      name: 'bwin',
      bonus: '100% jusqu\'à 100€',
      rating: 4.8,
      image: '/images/offers/bwin.webp',
      link: '/offres/bwin',
      url: 'https://bwin.fr/',
    },
    {
      id: 'netbet',
      name: 'NetBet',
      bonus: '150% jusqu\'à 150€',
      rating: 4.6,
      image: '/images/offers/netbet.webp',
      link: '/offres/netbet',
      url: 'https://www.netbet.fr/',
    },
    {
      id: 'genybet',
      name: 'Genybet',
      bonus: '200% jusqu\'à 200€',
      rating: 4.7,
      image: '/images/offers/genybet.webp',
      link: '/offres/genybet',
      url: 'https://www.genybet.fr/',
    },
  ];

  const statsData = [
    { month: 'Jan', offres: 45, utilisateurs: 12000 },
    { month: 'Fév', offres: 52, utilisateurs: 15000 },
    { month: 'Mar', offres: 48, utilisateurs: 18000 },
    { month: 'Avr', offres: 61, utilisateurs: 21000 },
    { month: 'Mai', offres: 58, utilisateurs: 24000 },
    { month: 'Juin', offres: 65, utilisateurs: 27000 },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Bookmakers Vérifiés',
      description: 'Tous les sites de paris sportifs sont soigneusement vérifiés et testés par notre équipe',
    },
    {
      icon: Clock,
      title: 'Retraits Rapides',
      description: 'Trouvez les bookmakers avec les délais de paiement les plus rapides du marché',
    },
    {
      icon: Gift,
      title: 'Bonus Exclusifs',
      description: 'Accès à des offres spéciales et des bonus que vous ne trouverez nulle part ailleurs',
    },
    {
      icon: Award,
      title: 'Analyses Détaillées',
      description: 'Avis complets avec toutes les informations dont vous avez besoin pour décider',
    },
  ];

  const heroAnchors = [
    { id: 'offres', label: 'Meilleures Offres', icon: Star },
    { id: 'comparaison', label: 'Comparer', icon: TrendingUp },
    { id: 'bonus', label: 'Bonus', icon: Gift },
    { id: 'analyses', label: 'Analyses', icon: Award },
    { id: 'classement', label: 'Classement', icon: TrendingUp },
    { id: 'apropos', label: 'À Propos', icon: Shield },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Primorixx',
    url: 'https://primorixx.com',
    description:
      'Les meilleurs sites de paris sportifs en France. Trouvez des analyses détaillées, des comparaisons et les offres les plus généreuses du marché.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://primorixx.com/comparaison?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Layout
      title="Meilleurs Sites de Paris Sportifs en France"
      description="Trouvez les meilleurs sites de paris sportifs en France avec des analyses détaillées, des comparaisons et des offres exclusives. Bonus généreux et bookmakers vérifiés."
      canonicalUrl="/"
      jsonLd={jsonLd}
    >
      <div className="min-h-screen">
      {/* Hero Section with 6 Anchor Links */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/heromain1.webp"
            alt="Hero"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-bg via-dark-bg/50 to-dark-bg" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 gradient-text">
              Les Meilleurs Sites de Paris Sportifs en France
            </h1>
            <p className="text-xl md:text-2xl text-dark-muted max-w-3xl mx-auto mb-8">
              Trouvez les offres les plus généreuses et les bookmakers les plus fiables.
              Nous analysons chaque détail pour que vous puissiez parier en toute sécurité.
            </p>
          </div>

          {/* 6 Anchor Links */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto mb-12">
            {heroAnchors.map((anchor) => {
              const Icon = anchor.icon;
              return (
                <a
                  key={anchor.id}
                  href={`#${anchor.id}`}
                  className="group flex flex-col items-center justify-center p-6 bg-dark-card/50 backdrop-blur-sm border border-dark-border rounded-xl hover:border-dark-accent transition-all duration-300 hover:transform hover:scale-105"
                >
                  <Icon className="w-8 h-8 mb-3 text-dark-accent group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium text-white text-center">
                    {anchor.label}
                  </span>
                </a>
              );
            })}
          </div>

          <div className="text-center">
            <Link
              to="/comparaison"
              className="inline-flex items-center px-8 py-4 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Comparer les Offres
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="offres" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Meilleures Offres du Mois
            </h2>
            <p className="text-dark-muted text-lg">
              Nous avons sélectionné les offres les plus avantageuses pour vous
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {topOffers.map((offer, index) => (
              <div
                key={offer.id}
                className="bg-dark-card border border-dark-border rounded-xl overflow-hidden hover:border-dark-accent transition-all duration-300 hover:transform hover:scale-105 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-48 bg-gradient-to-br from-dark-accent/20 to-dark-surface overflow-hidden">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-full object-contain p-4"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 flex items-center space-x-1 bg-dark-card/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-semibold text-white">
                      {offer.rating}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {offer.name}
                  </h3>
                  <p className="text-dark-accent font-semibold text-lg mb-4">
                    {offer.bonus}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {offer.url && (
                      <a
                        href={offer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-all duration-300 hover:scale-105"
                      >
                        Parier Maintenant
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </a>
                    )}
                    <Link
                      to={offer.link}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-medium transition-colors border border-dark-border"
                    >
                      Voir l'Analyse
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparaison" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Comparaison Rapide
            </h2>
            <p className="text-dark-muted text-lg">
              Comparez les principales caractéristiques de chaque offre
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block mb-8">
            <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-dark-surface border-b border-dark-border">
                      <th className="px-6 py-4 text-left">
                        <span className="text-white font-semibold text-lg">Bookmaker</span>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <span className="text-white font-semibold text-lg flex items-center">
                          <Gift className="w-5 h-5 mr-2 text-dark-accent" />
                          Bonus
                        </span>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <span className="text-white font-semibold text-lg flex items-center">
                          <Star className="w-5 h-5 mr-2 text-yellow-400 fill-yellow-400" />
                          Note
                        </span>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <span className="text-white font-semibold text-lg flex items-center">
                          <Clock className="w-5 h-5 mr-2 text-dark-accent" />
                          Retrait
                        </span>
                      </th>
                      <th className="px-6 py-4 text-center">
                        <span className="text-white font-semibold text-lg">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topOffers.map((offer, index) => (
                      <tr
                        key={offer.id}
                        className={`border-b border-dark-border transition-colors ${
                          index === 0
                            ? 'bg-dark-accent/5'
                            : 'hover:bg-dark-surface/50'
                        } ${index === topOffers.length - 1 ? 'border-b-0' : ''}`}
                      >
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <div className="w-14 h-14 bg-dark-surface rounded-lg p-2 border border-dark-border flex items-center justify-center overflow-hidden">
                                <img
                                  src={offer.image}
                                  alt={offer.name}
                                  className="w-full h-full object-contain"
                                  loading="lazy"
                                />
                              </div>
                              {index === 0 && (
                                <div className="absolute -top-1 -right-1 bg-yellow-400 text-dark-bg text-xs font-bold px-2 py-0.5 rounded-full">
                                  #1
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-white font-semibold text-lg">
                                {offer.name}
                              </div>
                              {index === 0 && (
                                <div className="text-dark-accent text-sm">
                                  Le Plus Populaire
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-dark-accent font-semibold text-base">
                            {offer.bonus}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < Math.floor(offer.rating)
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-dark-border'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-white font-semibold">
                              {offer.rating}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-dark-muted">24-48h</span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col gap-2">
                            {offer.url && (
                              <a
                                href={offer.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-4 py-2 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-medium transition-colors text-sm"
                              >
                                Parier Maintenant
                              </a>
                            )}
                            <Link
                              to={offer.link}
                              className="inline-flex items-center justify-center px-4 py-2 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-medium transition-colors text-sm border border-dark-border"
                            >
                              Voir les Détails
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 mb-8">
            {topOffers.map((offer, index) => (
              <div
                key={offer.id}
                className={`bg-dark-card border rounded-xl overflow-hidden ${
                  index === 0
                    ? 'border-dark-accent shadow-lg shadow-dark-accent/20'
                    : 'border-dark-border'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="relative">
                      <div className="w-16 h-16 bg-dark-surface rounded-lg p-2 border border-dark-border flex items-center justify-center overflow-hidden">
                        <img
                          src={offer.image}
                          alt={offer.name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                      {index === 0 && (
                        <div className="absolute -top-1 -right-1 bg-yellow-400 text-dark-bg text-xs font-bold px-2 py-0.5 rounded-full">
                          #1
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-semibold text-lg mb-1">
                        {offer.name}
                      </div>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3 h-3 ${
                              i < Math.floor(offer.rating)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-dark-border'
                            }`}
                          />
                        ))}
                        <span className="text-white text-sm ml-1">
                          {offer.rating}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-dark-surface/50 rounded-lg p-3">
                      <div className="text-dark-muted text-xs mb-1">Bonus</div>
                      <div className="text-dark-accent font-semibold text-sm">
                        {offer.bonus}
                      </div>
                    </div>
                    <div className="bg-dark-surface/50 rounded-lg p-3">
                      <div className="text-dark-muted text-xs mb-1">Retrait</div>
                      <div className="text-white font-semibold text-sm">
                        24-48h
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    {offer.url && (
                      <a
                        href={offer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center px-4 py-2.5 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-medium transition-colors"
                      >
                        Jouer Maintenant
                      </a>
                    )}
                    <Link
                      to={offer.link}
                      className="block w-full text-center px-4 py-2.5 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-medium transition-colors border border-dark-border"
                    >
                      Voir les Détails
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/comparaison"
              className="inline-flex items-center px-6 py-3 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
            >
              Voir la Comparaison Complète
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section id="bonus" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Bonus Exclusifs
            </h2>
            <p className="text-dark-muted text-lg">
              Profitez d'offres spéciales disponibles uniquement via notre site
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Bonus de Bienvenue',
                description: 'Jusqu\'à 200% sur le premier dépôt',
                icon: Gift,
              },
              {
                title: 'Bonus Sans Dépôt',
                description: 'Crédit gratuit pour tester',
                icon: Zap,
              },
              {
                title: 'Cashback Hebdomadaire',
                description: 'Récupérez une partie des pertes',
                icon: TrendingUp,
              },
            ].map((bonus, index) => {
              const Icon = bonus.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-dark-accent/20 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-dark-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {bonus.title}
                  </h3>
                  <p className="text-dark-muted">{bonus.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Analytics/Stats Section */}
      <section id="analyses" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Statistiques et Tendances
            </h2>
            <p className="text-dark-muted text-lg">
              Suivez les tendances du marché des sites de paris sportifs
            </p>
          </div>

          <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              Croissance des Offres et des Utilisateurs
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={statsData}>
                <XAxis dataKey="month" stroke="#a0a0a0" />
                <YAxis stroke="#a0a0a0" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #2a2a3e',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="offres"
                  stroke="#6366f1"
                  strokeWidth={2}
                  name="Offres"
                />
                <Line
                  type="monotone"
                  dataKey="utilisateurs"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  name="Utilisateurs"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Ranking Section */}
      <section id="classement" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Classement des Meilleurs
            </h2>
            <p className="text-dark-muted text-lg">
              Les bookmakers les mieux notés par nos experts
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {topOffers.map((offer, index) => (
              <div
                key={offer.id}
                className="relative bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
              >
                {index === 0 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-dark-bg px-4 py-1 rounded-full text-sm font-bold">
                    #1 Le Plus Populaire
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-3xl font-bold text-dark-muted">
                    #{index + 1}
                  </div>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.floor(offer.rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-dark-border'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full h-32 object-contain mb-4"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold text-white mb-4">
                  {offer.name}
                </h3>
                <div className="flex flex-col gap-2">
                  {offer.url && (
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Parier Maintenant
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  )}
                  <Link
                    to={offer.link}
                    className="inline-flex items-center justify-center px-4 py-2 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-medium transition-colors text-sm border border-dark-border"
                  >
                    Voir l'Analyse
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="apropos" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Pourquoi Choisir Primorixx?
            </h2>
            <p className="text-dark-muted text-lg">
              Nous offrons des analyses impartiales et des informations détaillées
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300 text-center"
                >
                  <div className="w-16 h-16 bg-dark-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-dark-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-dark-muted text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/apropos"
              className="inline-flex items-center px-8 py-4 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
            >
              En Savoir Plus Sur Nous
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Sports Types Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Sports Disponibles
            </h2>
            <p className="text-dark-muted text-lg">
              Explorez différentes catégories de sports sur les meilleurs bookmakers
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                icon: Trophy,
                title: 'Football',
                description: 'Ligue 1, Champions League',
                count: '1000+',
              },
              {
                icon: Trophy,
                title: 'Tennis',
                description: 'Roland-Garros, Wimbledon',
                count: '500+',
              },
              {
                icon: Trophy,
                title: 'Basketball',
                description: 'NBA, Euroleague',
                count: '300+',
              },
              {
                icon: Trophy,
                title: 'Autres Sports',
                description: 'Rugby, Handball, Volley',
                count: '200+',
              },
            ].map((game, index) => {
              const Icon = game.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 text-center hover:border-dark-accent transition-all duration-300 hover:scale-105"
                >
                  <div className="w-16 h-16 bg-dark-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-dark-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {game.title}
                  </h3>
                  <p className="text-dark-muted text-sm mb-2">
                    {game.description}
                  </p>
                  <span className="text-dark-accent font-bold">{game.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Méthodes de Paiement
            </h2>
            <p className="text-dark-muted text-lg">
              Dépôts et retraits rapides et sécurisés
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { name: 'Carte Bancaire', icon: CreditCard },
              { name: 'PayPal', icon: Smartphone },
              { name: 'Skrill', icon: Zap },
              { name: 'Neteller', icon: DollarSign },
              { name: 'Virement Bancaire', icon: CreditCard },
              { name: 'Crypto', icon: Lock },
            ].map((method, index) => {
              const Icon = method.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 text-center hover:border-dark-accent transition-all duration-300"
                >
                  <Icon className="w-10 h-10 text-dark-accent mx-auto mb-3" />
                  <p className="text-white text-sm font-medium">{method.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile Apps Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-dark-accent/20 rounded-2xl mb-6">
                <Smartphone className="w-8 h-8 text-dark-accent" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
                Pariez sur Votre Mobile
              </h2>
              <p className="text-dark-muted text-lg mb-6">
                Accédez aux meilleurs bookmakers directement depuis votre smartphone ou
                tablette. Applications natives et versions mobiles optimisées pour une
                expérience parfaite où que vous soyez.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Applications natives pour iOS et Android',
                  'Interface responsive et rapide',
                  'Bonus exclusifs pour mobile',
                  'Paiements sécurisés dans l\'app',
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center space-x-3 text-dark-muted"
                  >
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
              <div className="grid grid-cols-2 gap-4">
                {topOffers.map((offer) => (
                  <div
                    key={offer.id}
                    className="bg-dark-surface rounded-lg p-4 text-center"
                  >
                    <img
                      src={offer.image}
                      alt={offer.name}
                      className="h-16 w-auto mx-auto mb-2 object-contain"
                      loading="lazy"
                    />
                    <p className="text-white text-sm font-medium">
                      {offer.name}
                    </p>
                    <p className="text-dark-accent text-xs mt-1">App Disponible</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Questions Fréquentes
            </h2>
            <p className="text-dark-muted text-lg">
              Réponses rapides aux questions les plus courantes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                question: 'Les bookmakers sont-ils sûrs?',
                answer:
                  'Oui, tous les bookmakers recommandés possèdent des licences valides et utilisent la technologie de cryptage SSL.',
              },
              {
                question: 'Combien de temps prend un retrait?',
                answer:
                  'Cela dépend de la méthode choisie. Portefeuilles électroniques: 24-48h, Cartes: 2-5 jours ouvrables.',
              },
              {
                question: 'Y a-t-il des frais pour utiliser les services?',
                answer:
                  'Non, nos services sont entièrement gratuits pour tous les utilisateurs.',
              },
              {
                question: 'Comment fonctionne le bonus?',
                answer:
                  'Les bonus sont crédités automatiquement après le premier dépôt éligible.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
              >
                <div className="flex items-start space-x-3 mb-3">
                  <HelpCircle className="w-5 h-5 text-dark-accent flex-shrink-0 mt-0.5" />
                  <h3 className="text-lg font-bold text-white">{faq.question}</h3>
                </div>
                <p className="text-dark-muted text-sm ml-8">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/faq"
              className="inline-flex items-center px-6 py-3 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
            >
              Voir Toutes les Questions
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Ce Que Disent les Parieurs
            </h2>
            <p className="text-dark-muted text-lg">
              Expériences réelles de parieurs français
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: 'Jean Dupont',
                rating: 5,
                text: 'J\'ai trouvé les meilleurs bonus grâce à ce site. Je recommande!',
                bookmaker: 'bwin',
              },
              {
                name: 'Marie Martin',
                rating: 5,
                text: 'Analyses très détaillées et utiles. Cela m\'a beaucoup aidé dans mon choix.',
                bookmaker: 'Genybet',
              },
              {
                name: 'Pierre Bernard',
                rating: 4,
                text: 'Site fiable avec des informations à jour. Excellent travail!',
                bookmaker: 'NetBet',
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < testimonial.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-dark-border'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-dark-muted mb-4 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-dark-muted text-sm">{testimonial.bookmaker}</p>
                  </div>
                  <MessageCircle className="w-8 h-8 text-dark-accent opacity-50" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Dernières Mises à Jour
            </h2>
            <p className="text-dark-muted text-lg">
              Restez informé des nouveautés du marché
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Nouveau Bonus de 200%',
                description:
                  'Genybet a lancé une offre exclusive pour les nouveaux parieurs',
                date: '15 Déc 2024',
                type: 'Bonus',
              },
              {
                title: 'Nouvelle Licence Approuvée',
                description:
                  'bwin a reçu une licence officielle pour opérer en France',
                date: '10 Déc 2024',
                type: 'Actualités',
              },
              {
                title: 'App Mobile Lancée',
                description:
                  'NetBet met à disposition une application pour iOS et Android',
                date: '5 Déc 2024',
                type: 'Apps',
              },
            ].map((update, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-dark-accent/20 text-dark-accent text-xs font-semibold rounded-full">
                    {update.type}
                  </span>
                  <span className="text-dark-muted text-xs">{update.date}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {update.title}
                </h3>
                <p className="text-dark-muted text-sm">{update.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Trust Section */}
      <section className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Sécurité et Confiance
            </h2>
            <p className="text-dark-muted text-lg">
              Votre sécurité est notre priorité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Lock,
                title: 'Cryptage SSL',
                description:
                  'Tous les bookmakers utilisent une technologie de cryptage avancée pour protéger vos données',
              },
              {
                icon: Shield,
                title: 'Licences Vérifiées',
                description:
                  'Seuls les bookmakers avec des licences valides et vérifiées sont recommandés',
              },
              {
                icon: Check,
                title: 'Jeu Responsable',
                description:
                  'Nous promouvons le jeu responsable et offrons des outils de contrôle',
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-8 text-center hover:border-dark-accent transition-all duration-300"
                >
                  <div className="w-20 h-20 bg-dark-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-10 h-10 text-dark-accent" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {item.title}
                  </h3>
                  <p className="text-dark-muted">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tools & Calculators Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              Outils Utiles
            </h2>
            <p className="text-dark-muted text-lg">
              Utilisez nos outils pour prendre de meilleures décisions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: TrendingUp,
                title: 'Calculateur de Bonus',
                description:
                  'Calculez combien vous recevrez avec différents types de bonus',
              },
              {
                icon: Clock,
                title: 'Délai de Retrait',
                description:
                  'Comparez les délais de retrait entre différentes méthodes',
              },
              {
                icon: Star,
                title: 'Comparateur Rapide',
                description: 'Comparez rapidement plusieurs bookmakers côte à côte',
              },
            ].map((tool, index) => {
              const Icon = tool.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300 hover:scale-105"
                >
                  <div className="w-14 h-14 bg-dark-accent/20 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-dark-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {tool.title}
                  </h3>
                  <p className="text-dark-muted text-sm mb-4">
                    {tool.description}
                  </p>
                  <Link
                    to="/comparaison"
                    className="inline-flex items-center text-dark-accent hover:text-dark-accentHover font-medium text-sm"
                  >
                    Utiliser l'Outil
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-dark-accent/20 via-dark-surface to-dark-bg border-t border-dark-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center bg-dark-card border border-dark-border rounded-2xl p-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 gradient-text">
              Prêt à Commencer?
            </h2>
            <p className="text-xl text-dark-muted mb-8">
              Trouvez le bookmaker parfait pour vous et commencez à parier dès aujourd'hui
              avec les meilleurs bonus disponibles
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/comparaison"
                className="inline-flex items-center justify-center px-8 py-4 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Comparer les Offres
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/apropos"
                className="inline-flex items-center justify-center px-8 py-4 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-semibold transition-all duration-300 border border-dark-border"
              >
                En Savoir Plus
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </Layout>
  );
};

export default Home;

